/**
 * Copyright (c) 2026-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { type ErrorEvent, type EventHint, init } from "@sentry/react";

export type SentryConfig = {
  ndlaEnvironment: string;
  sentrydsn: string;
  componentName: string;
  componentVersion: string;
};

type BeforeSend = (event: ErrorEvent, hint: EventHint) => ErrorEvent | null;

type SentryIgnore = {
  error: string;
  exact?: boolean;
};

// Chrome, Safari and Firefox wording. Sentry appends the hostname, e.g. "Failed to fetch (api.ndla.no)".
const fetchFailureRegex =
  /^(\[Network error\]: )?(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)( \(.+\))?$/;

// Mostly the user's network, but keep a sample so outages still show up as a spike.
const FETCH_FAILURE_SAMPLE_RATE = 0.01;

const sentryIgnoreErrors: SentryIgnore[] = [
  // https://github.com/getsentry/sentry/issues/61469
  { error: 'Object.prototype.hasOwnProperty.call(o,"telephone")' },
  { error: 'Object.prototype.hasOwnProperty.call(e,"telephone")' },
  // https://github.com/matomo-org/matomo/issues/22836
  { error: "'get' on proxy: property 'javaEnabled' is a read-only and non-configurable data property" },
  // Based on Sentry issues. ChromeOS specific errors.
  { error: "Request timeout isMathOcrAvailable" },
  { error: "Request timeout getDictionariesByLanguageId" },
  { error: "Request timeout getSupportScreenShot" },
  { error: "Request timeout isDictateAvailable" },
  { error: "Request timeout isPredictionAvailable" },
  { error: "Request timeout dictionariesDistributor.getValue" },
  { error: "Request timeout speechVoicesDistributor.getValue" },
  { error: "Request timeout userDistributor.getValue" },
  { error: "Request timeout predictionDistributor.getValue" },
  { error: "Request timeout dictateStateDistributor.getValue" },
  { error: "Request timeout nn-NO_wordsDistributor.getValue" },
  { error: "Request timeout availableTextCheckLanguagesDistributor.getValue" },
  { error: "Request timeout es_wordsDistributor.getValue" },
  { error: "Request timeout lettersVoicesDistributor.getValue" },
  { error: "Request timeout topicsDistributor.getValue" },
  { error: "Request timeout availableLanguagesDistributor.getValue" },
  { error: "Request timeout ac_wordsDistributor.getValue" },
  { error: "Request timeout nb-NO_wordsDistributor.getValue" },
  { error: "Request timeout ua_wordsDistributor.getValue" },
  { error: "Request timeout en_wordsDistributor.getValue" },
  { error: "Request timeout appSettingsDistributor.getValue" },
  { error: "Request timeout DefineExpirationForLanguagePacks.getValue" },
  { error: "Request timeout textCheckersDistributor.getValue" },
  { error: "Request timeout de_wordsDistributor.getValue" },
  { error: "Request timeout fr_wordsDistributor.getValue" },
  { error: "Request timeout ru_wordsDistributor.getValue" },
  { error: "Request timeout ToolbarStatus" },
  { error: "Request timeout getSelectedText" },
  // https://github.com/getsentry/sentry-javascript/issues/3440
  { error: "Object Not Found Matching Id" },
];

export const createBeforeSend =
  (isInformationalError: (exception: unknown) => boolean): BeforeSend =>
  (event, hint) => {
    if (isInformationalError(hint.originalException)) return null;

    const message =
      event.message || event?.exception?.values?.[0]?.value || (hint?.originalException as Error | undefined)?.message;
    if (typeof message !== "string") return event;

    // Extension error filtering
    const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
    const hasExtensionFrame = frames.some((frame) => {
      const filename = frame?.filename || "";
      return (
        filename.startsWith("chrome-extension://") ||
        filename.startsWith("moz-extension://") ||
        filename.includes("extensions::")
      );
    });

    const isExtensionError =
      hasExtensionFrame || message.includes("chrome-extension://") || message.includes("moz-extension://");

    if (isExtensionError) return null;

    const ignoreEntry = sentryIgnoreErrors.find((ignoreEntry) => {
      if (ignoreEntry.exact) {
        return message === ignoreEntry.error;
      }
      return message.includes(ignoreEntry.error);
    });

    if (ignoreEntry) return null;

    // OneNote can fail in a million ways. They are probably not our problem.
    if (document.referrer && document.referrer.includes("noc-onenote.officeapps.live.com")) {
      return null;
    }

    if (fetchFailureRegex.test(message)) {
      if (!navigator.onLine || Math.random() >= FETCH_FAILURE_SAMPLE_RATE) return null;
      event.tags = { ...event.tags, sampled: "fetch-failure" };
    }

    return event;
  };

export const initSentry = (config: SentryConfig, beforeSend: BeforeSend) => {
  if (config.ndlaEnvironment === "local" || config.ndlaEnvironment === "dev") {
    // Skipping sentry initialization in local and dev environments
    return;
  }

  if (!config.sentrydsn) return;

  const release = `${config.componentName}@${config.componentVersion}`;

  init({
    dsn: config.sentrydsn,
    environment: config.ndlaEnvironment,
    normalizeDepth: 20,
    release,
    beforeSend,
    integrations: [],
    dataCollection: { userInfo: false },
  });
};
